import React, { useEffect, useState } from "react"
import {
  PersonOutline,
  EmailOutlined,
  LockOutlined,
  LockResetOutlined,
  BusinessOutlined,
  ImageOutlined,
  AdminPanelSettingsOutlined,
} from "@mui/icons-material"
import PasswordOutlinedIcon from "@mui/icons-material/PasswordOutlined"
import { useParams, useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useSelector, useDispatch } from "react-redux"
import Select from "react-select"
import { setBreadcrumbItems } from "../../store/actions"
import withRouter from "components/Common/withRouter"
import { editProfile, resetProfileFlag } from "../../store/actions"
import axios from "axios"
import config from "../../config/config"
import SnackbarComponent from "components/SnackBar"
import MFAInput from "components/Common/MFAInput"
import PrimaryButton from "newcomponents/primary-button"

const roleOptions = [
  { value: "viewer", label: "Viewer" },
  { value: "moderator", label: "Moderator" },
  { value: "admin", label: "Admin" },
]

const CreatePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const mode = useSelector(state => state.Layout.layoutMode)
  const userData = useSelector(state => state.Login.userData)
  const [user, setUser] = useState(null) // State for storing user data if updating
  const [loading, setLoading] = useState(false) // Loading state
  const [userAvatar, setUserAvatar] = useState("") // Avatar state
  const [err, setErr] = useState(null)
  const [mfaModalOpen, setMfaModalOpen] = useState(false)
  const [mfaError, setMfaError] = useState("")
  const [pendingUserData, setPendingUserData] = useState(null)
  const [success, setSuccess] = useState(null)
  const dispatch = useDispatch()
  const [originalUserData, setOriginalUserData] = useState(null)

  const organizations = useSelector(
    state => state.organizationReducer.organizations,
  )
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )

  useEffect(() => {
    if (id) {
      fetchUser(id) // Fetch user data if id exists
    }
  }, [id])

  useEffect(() => {
    // Fetch organizations and set them in the state

    if (err) {
      const timer = setTimeout(() => {
        setErr(null)
      }, 3000)
    }

    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null)
      }, 4000)
    }
  }, [err, success])

  useEffect(() => {
    const breadcrumbItems = [
      { title: "Reputation Management", link: "/" },
      { title: "Users", link: "/organizations-list" },
      { title: id ? "Update User" : "Create User", link: "#" },
    ]
    dispatch(setBreadcrumbItems(`${id ? "Update" : "Create"}`, breadcrumbItems))
  }, [user])

  // Fetch the user data for editing
  const fetchUser = async userId => {
    setLoading(true)
    try {
      const response = await axios.get(
        `${config.backendUrl}/user?userId=${userId}`,
      )
      setUser(response.data.user) // Set the user data for updating
      setUserAvatar(response.data.user.avatar)
      setLoading(false)
      setOriginalUserData(response.data.user)
    } catch (error) {
      console.error("Error fetching user data:", error)
      setLoading(false)
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      user: {
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        newPassword: "",
        confirmPassword: "",
        orgs:
          user?.OrganizationAssignments?.map(org => org.Organization.id) || [],
        avatar: null,
        role: user?.role || "viewer",
      },
    },
    validationSchema: Yup.object({
      user: Yup.object({
        firstName: Yup.string()
          .min(2, "First name must be at least 2 characters")
          .matches(/^[A-Za-z]+$/, "First name can only contain letters")
          .when([], {
            is: () => !id,
            then: schema => schema.required("Please Enter First Name"),
            otherwise: schema => schema.notRequired(),
          }),
        lastName: Yup.string()
          .min(2, "Last name must be at least 2 characters")
          .matches(/^[A-Za-z]+$/, "Last name can only contain letters")
          .when([], {
            is: () => !id,
            then: schema => schema.required("Please Enter Last Name"),
            otherwise: schema => schema.notRequired(),
          }),
        email: Yup.string()
          .email("Invalid Email")
          .when([], {
            is: () => !id,
            then: schema => schema.required("Please Enter User Email"),
            otherwise: schema => schema.notRequired(),
          }),
        newPassword: Yup.string()
          .min(6, "Password must be at least 6 characters")
          .notRequired(), // Always optional
        confirmPassword: Yup.string().when("newPassword", {
          is: newPassword => !!newPassword, // Require confirmation only if newPassword is provided
          then: schema =>
            schema
              .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
              .required("Please Confirm Password"),
          otherwise: schema => schema.notRequired(),
        }),
        role: Yup.string()
          .oneOf(["viewer", "moderator", "admin"], "Invalid Role")
          .required("Please select a role"),
        orgs: Yup.array().when([], {
          is: () => !id, // Required only if id is not present (i.e., creation mode)
          then: schema =>
            schema
              .min(1, "Please select at least one organization")
              .required("Please select at least one organization"),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    }),
    onSubmit: async values => {
      setLoading(true)
      const updatedData = { ...values.user }

      // ✅ Compare with original data (for edit mode)
      if (id && originalUserData) {
        const hasChanges =
          updatedData.firstName !== originalUserData.firstName ||
          updatedData.lastName !== originalUserData.lastName ||
          updatedData.email !== originalUserData.email ||
          updatedData.role !== originalUserData.role ||
          JSON.stringify(updatedData.orgs) !==
            JSON.stringify(
              originalUserData.OrganizationAssignments?.map(
                org => org.Organization.id,
              ),
            ) ||
          (updatedData.newPassword && updatedData.newPassword.length > 0) ||
          userAvatar !== originalUserData.avatar

        if (!hasChanges) {
          setSuccess(null)
          setErr("No changes made") // Snackbar will show this
          setLoading(false)
          return
        }
      }
      if (
        values.user.newPassword !== "" &&
        values.user.confirmPassword !== ""
      ) {
        updatedData.newPassword = values.user.newPassword
        delete updatedData["confirmPassword"]
      } else {
        delete updatedData["newPassword"]
        delete updatedData["confirmPassword"]
      }

      if (userAvatar) {
        updatedData.avatar = userAvatar
      }

      setMfaModalOpen(true)
      setPendingUserData(updatedData)
    },
  })

  const handleMfaSubmit = async code => {
    setMfaError("")
    setLoading(true)
    console.log("Hello", code)
    try {
      // Call 2FA verification API using fetch
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || config.backendUrl}/auth/2fa/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData?.token}`,
          },
          body: JSON.stringify({ code, email: userData?.email }),
        },
      )
      const result = await response.json()
      if (response.status === 200 && result.userInfo) {
        // Proceed to save profile changes

        if (id) {
          console.log("User", pendingUserData)
          // If id exists, it's an update operation
          await axios
            .put(
              `${config.backendUrl}/user/update?userId=${id}`,
              { user: pendingUserData, organizationId: activeOrganization.id },
              {
                headers: {
                  Authorization: `Bearer ${userData.token}`,
                },
              },
            )
            .then(res => {
              console.log(res)
            })
          setSuccess("User Updated Successfully")
        } else {
          // If no id, it's a create operation
          await axios.post(
            `${config.backendUrl}/user/create`,
            {
              updatedData: pendingUserData,
              organizationId: activeOrganization.id,
            },
            {
              headers: {
                Authorization: `Bearer ${userData.token}`,
              },
            },
          )
          setSuccess("User Created Successfully")
        }
        setMfaModalOpen(false)
        setLoading(false)
        navigate("/dashboard/office")
      } else {
        setMfaError(result.message || "Invalid MFA code.")
        setLoading(false)
      }
    } catch (err) {
      setMfaError("Error verifying 2FA code.")
      setLoading(false)
    }
  }

  function dataURItoBlob(dataURI) {
    setLoading(true)
    let formData = new FormData()
    formData.append("file", dataURI)
    // setloading(true)
    axios
      .post(
        `${config.backendUrl}/aws/file?email=reputation-management`,
        formData,
      )
      .then(res => {
        setUserAvatar(res.data.url)
        setLoading(false)
        // console.log(res)
      })
      .catch(err => {
        console.log(err)
        setLoading(false)
      })
  }

  return (
    <div className="page-content p-0">
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />
      <MFAInput
        isOpen={mfaModalOpen}
        toggle={() => {
          setMfaModalOpen(false)
          // setPendingProfileData(null)
          setMfaError("")
        }}
        onSubmit={handleMfaSubmit}
        error={mfaError}
      />
      <Container
        style={{
          padding: "0px",
        }}
        fluid
      >
        {user && (
          <Row>
            <Col lg="12">
              <Card
                style={{
                  borderRadius: "20px",
                }}
              >
                <CardBody>
                  <div className="d-flex" style={{ gap: "20px" }}>
                    <div className="ms-3">
                      <img
                        src={userAvatar}
                        alt=""
                        style={{
                          objectFit: "cover",
                          width: "80px",
                          height: "80px",
                        }}
                        className="avatar-md rounded-circle img-thumbnail"
                      />
                    </div>
                    <div className="flex-grow-1 align-self-center">
                      <div className="text-muted">
                        <p className="mb-0">Id no: #{user?.id}</p>
                        <h5>
                          {`${user?.firstName || ""} ${user?.lastName || ""}`.trim()} ({user?.role})
                        </h5>
                        <p className="mb-1">{user?.email}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <input
                        id="avatarInput"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={e => dataURItoBlob(e.target.files[0])}
                      />

                      {/* Styled PrimaryButton */}
                      <PrimaryButton
                        style={{
                          height: "35px",
                          alignSelf: "center",
                          backgroundColor: mode === "dark" ? "#fff" : "#2990DF",
                          padding: "0px 10px",
                          borderRadius: "5px",
                          fontSize: "14px",
                          marginRight: "4px",
                          color: mode === "dark" ? "#000" : "#fff",
                          cursor: "pointer",
                        }}
                        type="button"
                        onClick={() =>
                          document.getElementById("avatarInput").click()
                        }
                      >
                        Change Avatar
                      </PrimaryButton>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        <div
          style={{
            borderRadius: "20px",
            backgroundColor: mode === "dark" ? "#252F40" : "#fff",
          }}
        >
          <Form
            className="form-horizontal"
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            <h4
              style={{
                paddingTop: "15px",
                marginLeft: "10px",
                color: mode === "dark" ? "#fff" : "#000",
              }}
              className="card-title mb-4"
            >
              {id ? "Update User" : "Create User"}
            </h4>

            <Card
              style={{
                boxShadow: "none",
                borderBottomLeftRadius: "20px",
                borderBottomRightRadius: "20px",
                backgroundColor: mode === "dark" ? "#252F40" : "#fff",
              }}
            >
              <CardBody>
                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <Label
                        style={{
                          color: mode === "dark" ? "#fff" : "#000",
                        }}
                        className="form-label"
                      >
                        First Name
                      </Label>
                      <div
                        style={{
                          position: "relative",
                          height: "45px",
                          borderRadius: "20px",
                        }}
                      >
                        <PersonOutline
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "10px",
                            transform: "translateY(-50%)",
                            color: mode === "dark" ? "#fff" : "#6c757d",
                            pointerEvents: "none",
                          }}
                        />
                        <Input
                          name="user.firstName"
                          type="text"
                          placeholder="Enter First Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user.firstName}
                          invalid={
                            validation.touched.user?.firstName &&
                            !!validation.errors.user?.firstName
                          }
                          autoComplete="off"
                          style={{ paddingLeft: "40px", height: "45px" }}
                        />
                        <FormFeedback>
                          {validation.errors.user?.firstName}
                        </FormFeedback>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="form-group">
                      <Label
                        style={{
                          color: mode === "dark" ? "#fff" : "#000",
                        }}
                        className="form-label"
                      >
                        Last Name
                      </Label>
                      <div style={{ position: "relative", height: "45px" }}>
                        <PersonOutline
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "10px",
                            transform: "translateY(-50%)",
                            color: mode === "dark" ? "#fff" : "#6c757d",
                            pointerEvents: "none",
                          }}
                        />
                        <Input
                          name="user.lastName"
                          type="text"
                          placeholder="Enter Last Name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user.lastName}
                          invalid={
                            validation.touched.user?.lastName &&
                            !!validation.errors.user?.lastName
                          }
                          autoComplete="off"
                          style={{ paddingLeft: "40px", height: "45px" }}
                        />
                        <FormFeedback>
                          {validation.errors.user?.lastName}
                        </FormFeedback>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Email */}
                <div
                  style={{
                    marginTop: "10px",
                  }}
                  className="form-group"
                >
                  <Label
                    style={{
                      color: mode === "dark" ? "#fff" : "#000",
                    }}
                    className="form-label"
                  >
                    Email
                  </Label>
                  <div style={{ position: "relative", height: "45px" }}>
                    <EmailOutlined
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "10px",
                        transform: "translateY(-50%)",
                        color: mode === "dark" ? "#fff" : "#6c757d",
                        pointerEvents: "none",
                      }}
                    />
                    <Input
                      name="user.email"
                      type="email"
                      placeholder="Enter Email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.user.email}
                      invalid={
                        validation.touched.user?.email &&
                        !!validation.errors.user?.email
                      }
                      style={{ paddingLeft: "40px", height: "45px" }}
                    />
                    <FormFeedback>{validation.errors.user?.email}</FormFeedback>
                  </div>
                </div>

                {/* Passwords */}
                <Row>
                  <Col md={6}>
                    <div style={{ marginTop: "10px" }} className="form-group">
                      <Label
                        style={{
                          color: mode === "dark" ? "#fff" : "#000",
                        }}
                        className="form-label"
                      >
                        New Password
                      </Label>
                      <div style={{ position: "relative", height: "45px" }}>
                        <PasswordOutlinedIcon
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "10px",
                            transform: "translateY(-50%)",
                            color: mode === "dark" ? "#fff" : "#6c757d",
                            pointerEvents: "none",
                          }}
                        />
                        <Input
                          name="user.newPassword"
                          type="password"
                          placeholder="Enter New Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user.newPassword}
                          invalid={
                            validation.touched.user?.newPassword &&
                            !!validation.errors.user?.newPassword
                          }
                          style={{ paddingLeft: "40px", height: "45px" }}
                        />
                        <FormFeedback>
                          {validation.errors.user?.newPassword}
                        </FormFeedback>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div
                      style={{
                        marginTop: "10px",
                      }}
                      className="form-group"
                    >
                      <Label
                        style={{
                          color: mode === "dark" ? "#fff" : "#000",
                        }}
                        className="form-label"
                      >
                        Confirm Password
                      </Label>
                      <div
                        style={{
                          position: "relative",
                          height: "45px",
                          borderRadius: "100px",
                        }}
                      >
                        <PasswordOutlinedIcon
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "10px",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            color: mode === "dark" ? "#fff" : "#6c757d",
                          }}
                        />
                        <Input
                          name="user.confirmPassword"
                          type="password"
                          placeholder="Confirm Password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user.confirmPassword}
                          invalid={
                            validation.touched.user?.confirmPassword &&
                            !!validation.errors.user?.confirmPassword
                          }
                          style={{ paddingLeft: "40px", height: "45px" }}
                        />
                        <FormFeedback>
                          {validation.errors.user?.confirmPassword}
                        </FormFeedback>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Organizations */}
                <Row>
                  <Col md={6}>
                    <div className="form-group">
                      <Label
                        style={{
                          marginTop: "10px",
                          color: mode === "dark" ? "#fff" : "#000",
                        }}
                        className="form-label d-flex align-items-center gap-1"
                      >
                        Assign Organizations
                      </Label>
                      <Select
                        options={organizations.map(org => ({
                          value: org.Organization.id,
                          label: org.Organization.name,
                        }))}
                        isMulti
                        name="user.orgs"
                        className="basic-multi-select"
                        classNamePrefix="select"
                        value={organizations
                          .filter(org =>
                            validation.values.user.orgs.includes(
                              org.Organization.id,
                            ),
                          )
                          .map(org => ({
                            value: org.Organization.id,
                            label: org.Organization.name,
                          }))}
                        onChange={selectedOptions =>
                          validation.setFieldValue(
                            "user.orgs",
                            selectedOptions.map(option => option.value),
                          )
                        }
                      />
                      {validation.touched.user?.orgs &&
                        validation.errors.user?.orgs && (
                          <FormFeedback style={{ display: "block" }}>
                            {validation.errors.user.orgs}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>

                  {/* User Role */}
                  <Col md={6}>
                    <div className="form-group">
                      <Label
                        style={{
                          color: mode === "dark" ? "#fff" : "#000",
                          marginTop: "10px",
                        }}
                        className="form-label d-flex align-items-center gap-1"
                      >
                        User Role
                      </Label>
                      <Select
                        name="user.role"
                        options={roleOptions}
                        value={roleOptions.find(
                          option =>
                            option.value === validation.values.user.role,
                        )}
                        onChange={selectedOption =>
                          validation.setFieldValue(
                            "user.role",
                            selectedOption.value,
                          )
                        }
                        onBlur={() =>
                          validation.setFieldTouched("user.role", true)
                        }
                        className="basic-single"
                        classNamePrefix="select"
                      />
                      {validation.touched.user?.role &&
                        validation.errors.user?.role && (
                          <FormFeedback style={{ display: "block" }}>
                            {validation.errors.user.role}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>
                </Row>

                <div className="text-center mt-4">
                  <PrimaryButton
                    style={{
                      width: "120px",
                      height: "40px",
                      padding: "0px 0px",
                      fontSize: "14px",
                      backgroundColor: "#2990DF",
                    }}
                    type="submit"
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : id ? (
                      "Update User"
                    ) : (
                      "Create User"
                    )}
                  </PrimaryButton>
                </div>
              </CardBody>
            </Card>
          </Form>
        </div>
      </Container>
    </div>
  )
}

export default withRouter(CreatePage)
